import { useIntl } from "react-intl";
import { useEffect, useState } from "react";
import classNames from "classnames";
import Link from "next/link";
import { Modal } from "@/components/common/modal/Modal";
import { useDebounce } from "@/hooks/useDebounce";
import { useFetchCompanyRate } from "@/components/common/searchBlock/hooks/useFetchCompanyRate";
import { CtaButton } from "@/components/common/ctaButton/CtaButton";
import { CheckmarkWithBackgroundIcon } from "@/public/icons/CheckmarkWithBackgroundIcon";
import { routes } from "@/routes/routes";
import styles from "./CompanyRateCodeModal.module.scss";

export type CompanyRateCodeModalProps = {
  isOpen: boolean;
  setIsOpen: (boolean) => void;
  updateCompanyRateCode: (string) => void;
};

export const CompanyRateCodeModal = ({
  isOpen,
  setIsOpen,
  updateCompanyRateCode,
}: CompanyRateCodeModalProps) => {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState<string | undefined>("");
  const [isValidCode, setIsValidCode] = useState<boolean | undefined>(
    undefined
  );
  const debouncedQuery = useDebounce(searchQuery, 500);
  const { companyRate, isLoading, error } = useFetchCompanyRate(debouncedQuery);

  useEffect(() => {
    if (error !== undefined) {
      setIsValidCode(false);
    }
    if (companyRate !== undefined) {
      setIsValidCode(true);
    }
    if (error === undefined && companyRate === undefined) {
      setIsValidCode(undefined);
    }
  }, [companyRate, error]);

  const setCompanyRateCode = () => {
    updateCompanyRateCode(companyRate?.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      overrideStyles={{
        modal: styles.modal,
        title: styles.title,
        body: styles.body,
      }}
      title={intl.formatMessage({ id: "companyRateCodeModalTitle" })}
      buttonType="button"
    >
      <div className={styles.input}>
        <div
          className={classNames(styles.label, {
            [styles.valid]: isValidCode === true,
            [styles.error]: isValidCode === false,
          })}
        >
          {intl.formatMessage({ id: "companyRateCodeInputLabel" })}
        </div>
        <div className={styles.inputGroup}>
          <input
            type="text"
            value={searchQuery}
            maxLength={20}
            placeholder={intl.formatMessage({
              id: "companyRateCodeInputPlaceholder",
            })}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={classNames(styles.inputField, {
              [styles.valid]: isValidCode === true,
              [styles.error]: isValidCode === false,
            })}
          />
          {isValidCode && (
            <div className={styles.validInputIcon}>
              <CheckmarkWithBackgroundIcon
                color={"#37837d"}
                size={"20"}
              ></CheckmarkWithBackgroundIcon>
            </div>
          )}
        </div>
        <div className={styles.errorMessage}>
          {isValidCode === false && (
            <p>{intl.formatMessage({ id: "invalidCompanyRateCode" })}</p>
          )}
        </div>
        <div className={styles.noCompanyRateCodeLink}>
          <Link href={routes.solutionPro}>
            <a>{intl.formatMessage({ id: "noCompanyRateCodeLabel" })}</a>
          </Link>
        </div>
      </div>
      <CtaButton
        label={intl.formatMessage({ id: "validate" })}
        isDisabled={!isValidCode || isLoading}
        onClick={setCompanyRateCode}
      ></CtaButton>
    </Modal>
  );
};
